import React, { useState, useEffect, useContext, useMemo } from 'react'
import { TextField, Box, MenuItem, Typography } from '@mui/material'
import { fetchData } from '../../../global/utils/fetch'
import {
  getDefectsDashboardData,
  getDefectsDashboardByTechnician,
  getDefectsDashboardByLocation,
} from './api'
import {
  DefectsDashboardData,
  DefectsByTechnicianData,
  DefectsByLocationData,
  DefectTypeData,
} from '../../../../../app/types/defects-dashboard'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import { IAlertStatusProps } from '../../../global/components/toast-alert/ToastNotification'
import Dashboard, {
  DashboardColumn,
} from '../../../global/components/dashboard/Dashboard'
import MultiLineDashboard, {
  AggregateEntry,
} from '../../../global/components/dashboard/MultiLineDashboard'
import formatCurrency from '../../../global/utils/format-currency-usd'
import { formatDateYyyyMmDd } from '../../../global/utils/date/date-utils-yyyy-mm-dd'
import PrimaryCheckbox from '../../../global/components/checkbox/Checkbox'

type ViewMode =
  | 'default'
  | 'by-technician'
  | 'by-location'
  | 'at-fault-by-technician'
  | 'at-fault-by-location'

/** Sentinel for "no specific technician/location selected" */
const ALL_GROUPS = 'all'

/** A defect type row, optionally carrying its change vs the comparison period */
type DefectRow = DefectTypeData & { delta?: number }

type Totals = {
  totalCount: number
  totalDamageCost: number
}

/** A technician or location, with its defect rows for one date range */
interface GroupData {
  key: string
  name: string
  defectsByType: DefectTypeData[]
  subtotals: Totals
}

function isTechnicianView(viewMode: ViewMode) {
  return viewMode === 'by-technician' || viewMode === 'at-fault-by-technician'
}

function isAtFaultView(viewMode: ViewMode) {
  return (
    viewMode === 'at-fault-by-technician' || viewMode === 'at-fault-by-location'
  )
}

function formatDelta(delta: number): string {
  return delta > 0 ? `+${delta}` : `${delta}`
}

/** More defects than the comparison period is bad, fewer is good. */
function deltaColor(delta: number): string | undefined {
  if (delta > 0) return 'red'
  if (delta < 0) return 'green'
  return undefined
}

/**
 * Parses a 'YYYY-MM-DD' input value as a LOCAL date. `new Date('2026-07-01')`
 * is parsed as UTC midnight, which lands on June 30 in US timezones and would
 * label the column with the wrong month.
 */
function parseLocalDate(value: string): Date | null {
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day)
}

function isLastDayOfMonth(date: Date): boolean {
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  return date.getDate() === lastDay
}

/**
 * Names a date range the way a reader would: 'Jul 2026' when it covers exactly
 * one calendar month, otherwise a compact '7/15 - 8/14'. Used to label the
 * change column so its meaning does not depend on a tooltip.
 */
function describeRange(startDate: string, endDate: string): string {
  const start = parseLocalDate(startDate)
  const end = parseLocalDate(endDate)
  if (!start || !end) return 'previous period'

  const isWholeMonth =
    start.getDate() === 1 &&
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear() &&
    isLastDayOfMonth(end)

  if (isWholeMonth) {
    return start.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    })
  }

  const short = (date: Date) =>
    date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
  return `${short(start)}\u2013${short(end)}`
}

// Default to current month
function getDefaultStartDate(): string {
  const date = new Date()
  date.setDate(1) // First day of current month
  return formatDateYyyyMmDd(date)
}

function getDefaultEndDate(): string {
  const date = new Date()
  date.setMonth(date.getMonth() + 1) // Next month
  date.setDate(0) // Last day of current month
  return formatDateYyyyMmDd(date)
}

// Default comparison to previous month
function getDefaultCompareStartDate(): string {
  const date = new Date()
  date.setMonth(date.getMonth() - 1) // Previous month
  date.setDate(1) // First day
  return formatDateYyyyMmDd(date)
}

function getDefaultCompareEndDate(): string {
  const date = new Date()
  date.setDate(0) // Last day of previous month
  return formatDateYyyyMmDd(date)
}

interface UseDefectsDataArgs {
  viewMode: ViewMode
  startDate: string
  endDate: string
  /** When false, no request is made (used for the comparison range while Compare is off) */
  enabled: boolean
}

/**
 * Fetches one date range's defect data. Lives here rather than inside the panel
 * so the parent can hold both ranges at once, which is what the delta column and
 * the shared technician/location selector need.
 */
function useDefectsData({
  viewMode,
  startDate,
  endDate,
  enabled,
}: UseDefectsDataArgs) {
  const { setToastStatus } = useContext(ToastNotificationContext)
  const [rows, setRows] = useState<DefectTypeData[]>([])
  const [groups, setGroups] = useState<GroupData[]>([])
  const [totals, setTotals] = useState<Totals | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(enabled)

  useEffect(() => {
    if (!enabled) {
      setRows([])
      setGroups([])
      setTotals(null)
      setIsLoading(false)
      return
    }

    // Guards against a slower earlier request overwriting a newer one
    let isStale = false

    async function fetchDashboardData() {
      setIsLoading(true)
      try {
        if (viewMode === 'default') {
          const data = await fetchData<DefectsDashboardData>(
            getDefectsDashboardData(startDate, endDate),
          )
          if (isStale) return
          setRows(data.defectsByType)
          setGroups([])
          setTotals(data.totals)
        } else if (isTechnicianView(viewMode)) {
          const data = await fetchData<DefectsByTechnicianData>(
            getDefectsDashboardByTechnician(
              isAtFaultView(viewMode),
              startDate,
              endDate,
            ),
          )
          if (isStale) return
          setRows([])
          setGroups(
            data.technicians.map((technician) => ({
              key: technician.technicianObjectId,
              name: technician.technicianName,
              defectsByType: technician.defectsByType,
              subtotals: technician.subtotals,
            })),
          )
          setTotals(data.totals)
        } else {
          const data = await fetchData<DefectsByLocationData>(
            getDefectsDashboardByLocation(
              isAtFaultView(viewMode),
              startDate,
              endDate,
            ),
          )
          if (isStale) return
          setRows([])
          setGroups(
            data.locations.map((location) => ({
              key: location.locationObjectId,
              name: location.locationName,
              defectsByType: location.defectsByType,
              subtotals: location.subtotals,
            })),
          )
          setTotals(data.totals)
        }
      } catch (err) {
        if (isStale) return
        console.error(err)
        setRows([])
        setGroups([])
        setTotals(null)
        setToastStatus((previous: IAlertStatusProps) => ({
          ...previous,
          isOpen: true,
          message: 'Failed to load defects dashboard data',
          severity: 'error',
        }))
      }
      if (!isStale) setIsLoading(false)
    }

    fetchDashboardData()

    return () => {
      isStale = true
    }
  }, [viewMode, startDate, endDate, enabled, setToastStatus])

  return { rows, groups, totals, isLoading }
}


interface DefectsDashboardPanelProps {
  startDate: string
  endDate: string
  /** e.g. "This period - Aug 2026". Omitted when only one panel is shown. */
  caption?: string
  onStartDateChange: (date: string) => void
  onEndDateChange: (date: string) => void
  isLoading: boolean
  columns: DashboardColumn<DefectRow>[]
  isGroupedView: boolean
  aggregateColumnHeader: string
  rows: DefectRow[]
  aggregates: AggregateEntry<DefectRow>[]
  totals?: React.ReactNode[]
}

/** Presentational: renders one date range's table. All data comes from the parent. */
function DefectsDashboardPanel({
  startDate,
  endDate,
  caption,
  onStartDateChange,
  onEndDateChange,
  isLoading,
  columns,
  isGroupedView,
  aggregateColumnHeader,
  rows,
  aggregates,
  totals,
}: DefectsDashboardPanelProps) {
  const dateControls = (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        mb: 2,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      {caption && (
        <Typography
          sx={{
            width: '100%',
            mb: -1,
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          {caption}
        </Typography>
      )}
      <TextField
        label='Start Date'
        type='date'
        value={startDate}
        InputLabelProps={{ shrink: true }}
        onChange={(e) => onStartDateChange(e.target.value)}
        disabled={isLoading}
        size='small'
      />
      <TextField
        label='End Date'
        type='date'
        value={endDate}
        InputLabelProps={{ shrink: true }}
        onChange={(e) => onEndDateChange(e.target.value)}
        disabled={isLoading}
        size='small'
      />
    </Box>
  )

  if (isGroupedView) {
    return (
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {dateControls}
        <MultiLineDashboard<DefectRow>
          columns={columns}
          aggregates={aggregates}
          aggregateColumnHeader={aggregateColumnHeader}
          isLoading={isLoading}
          totals={totals}
          totalsLabel='Grand Total'
          subtotalsLabel='Subtotal'
          getRowKey={(row, aggregateKey) => `${aggregateKey}-${row.typeKey}`}
          noDataMessage='No defects data available.'
        />
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {dateControls}
      <Dashboard<DefectRow>
        columns={columns}
        data={rows}
        isLoading={isLoading}
        totals={totals}
        totalsLabel='Total'
        getRowKey={(row) => row.typeKey}
        noDataMessage='No defects data available.'
      />
    </Box>
  )
}

function DefectsDashboard() {
  const [compareMode, setCompareMode] = useState<boolean>(false)
  const [viewMode, setViewMode] = useState<ViewMode>('default')
  const [selectedGroupKey, setSelectedGroupKey] = useState<string>(ALL_GROUPS)

  // Primary date range state
  const [startDate, setStartDate] = useState<string>(getDefaultStartDate())
  const [endDate, setEndDate] = useState<string>(getDefaultEndDate())

  // Comparison date range state
  const [compareStartDate, setCompareStartDate] = useState<string>(
    getDefaultCompareStartDate(),
  )
  const [compareEndDate, setCompareEndDate] = useState<string>(
    getDefaultCompareEndDate(),
  )

  const primary = useDefectsData({
    viewMode,
    startDate,
    endDate,
    enabled: true,
  })
  const comparison = useDefectsData({
    viewMode,
    startDate: compareStartDate,
    endDate: compareEndDate,
    enabled: compareMode,
  })

  /**
   * Zero-filled rows used to render a selected group that has no cases in one
   * of the two periods. Derived from data already on screen rather than
   * fetched: a group is only selectable if it appears in at least one period,
   * so that period's response already carries the full type list. Avoids a
   * network round trip that would leave the panel transiently - or, on
   * failure, permanently - empty.
   */
  const zeroTypeRows = useMemo<DefectTypeData[]>(() => {
    const source =
      primary.groups[0]?.defectsByType ??
      comparison.groups[0]?.defectsByType ??
      primary.rows
    return source.map((row) => ({
      typeKey: row.typeKey,
      typeDisplayName: row.typeDisplayName,
      count: 0,
      totalDamageCost: 0,
    }))
  }, [primary.groups, comparison.groups, primary.rows])

  const isGroupedView = viewMode !== 'default'
  const aggregateColumnHeader = isTechnicianView(viewMode)
    ? 'Technician'
    : 'Location'

  // A selection from one view mode is meaningless in another
  useEffect(() => {
    setSelectedGroupKey(ALL_GROUPS)
  }, [viewMode])

  // Union of both periods: a location present last month but not this month
  // still needs to be selectable.
  const groupOptions = useMemo(() => {
    const namesByKey = new Map<string, string>()
    for (const group of [...primary.groups, ...comparison.groups]) {
      if (!namesByKey.has(group.key)) namesByKey.set(group.key, group.name)
    }
    return Array.from(namesByKey, ([key, name]) => ({ key, name })).sort(
      (a, b) => a.name.localeCompare(b.name),
    )
  }, [primary.groups, comparison.groups])

  // A date change can drop the selected group out of both periods, which would
  // otherwise leave the select showing an out-of-range value and both tables empty.
  useEffect(() => {
    if (selectedGroupKey === ALL_GROUPS) return
    if (primary.isLoading || comparison.isLoading) return
    if (!groupOptions.some((option) => option.key === selectedGroupKey)) {
      setSelectedGroupKey(ALL_GROUPS)
    }
  }, [groupOptions, selectedGroupKey, primary.isLoading, comparison.isLoading])

  // Drives the change-column header, its tooltip, the panel captions and the
  // legend, so all four always name the same periods.
  const primaryLabel = useMemo(
    () => describeRange(startDate, endDate),
    [startDate, endDate],
  )
  const comparisonLabel = useMemo(
    () => describeRange(compareStartDate, compareEndDate),
    [compareStartDate, compareEndDate],
  )

  const comparisonCountsByType = useMemo(() => {
    const counts = new Map<string, number>()
    for (const row of comparison.rows) {
      counts.set(row.typeKey, row.count)
    }
    for (const group of comparison.groups) {
      for (const row of group.defectsByType) {
        counts.set(`${group.key}|${row.typeKey}`, row.count)
      }
    }
    return counts
  }, [comparison.rows, comparison.groups])

  const comparisonSubtotalsByGroup = useMemo(() => {
    const subtotals = new Map<string, number>()
    for (const group of comparison.groups) {
      subtotals.set(group.key, group.subtotals.totalCount)
    }
    return subtotals
  }, [comparison.groups])

  /**
   * Narrows to the selected technician/location. When the selection has no cases
   * in this period the backend omits it entirely, so fall back to an all-zero
   * block to keep the two panels aligned.
   */
  function resolveGroups(groups: GroupData[]): GroupData[] {
    if (selectedGroupKey === ALL_GROUPS) return groups

    const selected = groups.find((group) => group.key === selectedGroupKey)
    if (selected) return [selected]

    const option = groupOptions.find((o) => o.key === selectedGroupKey)
    if (!option || zeroTypeRows.length === 0) return []

    return [
      {
        key: option.key,
        name: option.name,
        defectsByType: zeroTypeRows,
        subtotals: { totalCount: 0, totalDamageCost: 0 },
      },
    ]
  }

  /** Deltas only make sense on the primary panel, against the comparison range. */
  function buildAggregates(
    groups: GroupData[],
    withDelta: boolean,
  ): AggregateEntry<DefectRow>[] {
    return resolveGroups(groups).map((group) => {
      const subtotalDelta =
        group.subtotals.totalCount -
        (comparisonSubtotalsByGroup.get(group.key) ?? 0)

      return {
        key: group.key,
        name: group.name,
        data: group.defectsByType.map((row) => ({
          ...row,
          delta: withDelta
            ? row.count -
              (comparisonCountsByType.get(`${group.key}|${row.typeKey}`) ?? 0)
            : undefined,
        })),
        subtotals: [
          group.subtotals.totalCount,
          ...(withDelta ? [formatDelta(subtotalDelta)] : []),
          formatCurrency(group.subtotals.totalDamageCost),
        ],
      }
    })
  }

  function buildRows(rows: DefectTypeData[], withDelta: boolean): DefectRow[] {
    return rows.map((row) => ({
      ...row,
      delta: withDelta
        ? row.count - (comparisonCountsByType.get(row.typeKey) ?? 0)
        : undefined,
    }))
  }

  function buildColumns(withDelta: boolean): DashboardColumn<DefectRow>[] {
    const columns: DashboardColumn<DefectRow>[] = [
      {
        Header: 'Defect Type',
        accessor: 'typeDisplayName',
      },
      {
        Header: 'Count',
        accessor: 'count',
        tooltip: 'Number of Defects of this Type',
        Cell: ({ row, value }) => {
          const isHighDefectCount = (value as number) > 10
          return {
            value,
            tooltip: isHighDefectCount
              ? `High defect count for ${row.typeDisplayName}`
              : undefined,
            color: isHighDefectCount ? 'red' : undefined,
          }
        },
      },
    ]

    if (withDelta) {
      columns.push({
        Header: `Change vs ${comparisonLabel}`,
        accessor: 'delta',
        tooltip:
          `${primaryLabel} count minus ${comparisonLabel} count. ` +
          `Higher means more defects than ${comparisonLabel}.`,
        Cell: ({ value }) => {
          const delta = (value as number) ?? 0
          return {
            value: formatDelta(delta),
            color: deltaColor(delta),
          }
        },
      })
    }

    columns.push({
      Header: 'Total Damage Cost',
      accessor: 'totalDamageCost',
      tooltip: 'Sum of Portal and Risk Connect damage costs for this Type',
      Cell: ({ value }) => ({
        value: formatCurrency(value as number),
      }),
    })

    return columns
  }

  /**
   * Dashboard renders a label cell then maps totals onto columns[idx + 1];
   * MultiLineDashboard maps grand totals onto columns[idx] after its own label
   * cell, hence the leading empty entry there.
   */
  function buildTotals(
    totals: Totals | null,
    withDelta: boolean,
    leadingBlank: boolean,
  ): React.ReactNode[] | undefined {
    if (!totals) return undefined

    const totalDelta =
      totals.totalCount - (comparison.totals?.totalCount ?? 0)

    return [
      ...(leadingBlank ? [''] : []),
      totals.totalCount,
      ...(withDelta ? [formatDelta(totalDelta)] : []),
      formatCurrency(totals.totalDamageCost),
    ]
  }

  const primaryColumns = buildColumns(compareMode)
  const comparisonColumns = buildColumns(false)

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <PrimaryCheckbox
          checked={compareMode}
          label='Compare'
          onChange={(_, checked) => setCompareMode(checked)}
        />
        <PrimaryCheckbox
          checked={viewMode === 'by-technician'}
          label='By Technician'
          onChange={(_, checked) =>
            setViewMode(checked ? 'by-technician' : 'default')
          }
        />
        <PrimaryCheckbox
          checked={viewMode === 'by-location'}
          label='By Location'
          onChange={(_, checked) =>
            setViewMode(checked ? 'by-location' : 'default')
          }
        />
        <PrimaryCheckbox
          checked={viewMode === 'at-fault-by-technician'}
          label='At Fault - By Technician'
          onChange={(_, checked) =>
            setViewMode(checked ? 'at-fault-by-technician' : 'default')
          }
        />
        <PrimaryCheckbox
          checked={viewMode === 'at-fault-by-location'}
          label='At Fault - By Location'
          onChange={(_, checked) =>
            setViewMode(checked ? 'at-fault-by-location' : 'default')
          }
        />
        {isGroupedView && (
          <TextField
            select
            label={aggregateColumnHeader}
            value={selectedGroupKey}
            onChange={(e) => setSelectedGroupKey(e.target.value)}
            size='small'
            sx={{ minWidth: 220 }}
          >
            <MenuItem value={ALL_GROUPS}>{`All ${aggregateColumnHeader}s`}</MenuItem>
            {groupOptions.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          flexDirection: compareMode ? 'row' : 'column',
        }}
      >
        <DefectsDashboardPanel
          startDate={startDate}
          endDate={endDate}
          caption={compareMode ? `This period \u00b7 ${primaryLabel}` : undefined}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          isLoading={primary.isLoading}
          columns={primaryColumns}
          isGroupedView={isGroupedView}
          aggregateColumnHeader={aggregateColumnHeader}
          rows={buildRows(primary.rows, compareMode)}
          aggregates={buildAggregates(primary.groups, compareMode)}
          totals={buildTotals(primary.totals, compareMode, isGroupedView)}
        />
        {compareMode && (
          <DefectsDashboardPanel
            startDate={compareStartDate}
            endDate={compareEndDate}
            caption={`Compared to \u00b7 ${comparisonLabel}`}
            onStartDateChange={setCompareStartDate}
            onEndDateChange={setCompareEndDate}
            isLoading={comparison.isLoading}
            columns={comparisonColumns}
            isGroupedView={isGroupedView}
            aggregateColumnHeader={aggregateColumnHeader}
            rows={buildRows(comparison.rows, false)}
            aggregates={buildAggregates(comparison.groups, false)}
            totals={buildTotals(comparison.totals, false, isGroupedView)}
          />
        )}
      </Box>
    </>
  )
}

export default DefectsDashboard
